/* eslint-disable @typescript-eslint/no-unused-vars */
import * as React from 'react';
import Alert from '@material-ui/lab/Alert';
import {
  Grid, Box, TextField, Divider, Card, CardContent, IconButton,
  Typography,
  CardHeader,
  Paper,
  CircularProgress,
  MenuItem,
} from '@material-ui/core';
import MoreVertIcon from '@material-ui/icons/MoreVert';
import useSWR from 'swr';
import type { AxiosError } from 'axios';
import axios from 'axios';
import { useSnackbar } from 'notistack';
import { debounce } from 'lodash';
import { useNavigate, useParams } from 'react-router-dom';
import type { GridColumns } from '@material-ui/data-grid';
import { DataGrid } from '@material-ui/data-grid';
import useEventListener from '../../lib/useEventListener';
import BoxProperty from '../metalCollection/RepairProperty';
import ActionMenu from '../global/ActionMenu';

interface IBoxNotes {
  note: string;
}
interface IScannedBox {
  boxId: string;
  trackingNumber: string;
  company: string;
  store: string;
  createdDate: string;
  lastModified: string;
  boxNotes: IBoxNotes[] | [];
}

interface IScan {
  scanBody: string;
  scanType: string;
  scanLocation: string;
  created: string;
  scannedBy: string;
}

interface IBoxRepair {
  repairId: string;
  barcode: string;
  storeRepairNumber: string;
  facility: string;
  location: string;
  startDate: string;
  lastModified: string;
  assignedTo: string;
  boxNotes: [IBoxNotes][] | [];
}

const BoxDetail: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const { boxTrackingNumber } = useParams();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [repairBarcode, setRepairBarcode] = React.useState<string | null>(null);

  const [trackingNumber, setTrackingNumber] = React.useState<string | undefined>(boxTrackingNumber);
  const [boxId, setBoxId] = React.useState<string>();
  const [getBoxProcessing, setGetBoxProcessing] = React.useState<boolean>(boxTrackingNumber !== undefined);

  const handleActionMenuClick = (event: React.MouseEvent<HTMLButtonElement>, id: string) => {
    if (!anchorEl) {
      setRepairBarcode(id);
      setAnchorEl(event.currentTarget);
    }
  };

  const handleClose = () => {
    setRepairBarcode(null);
    setAnchorEl(null);
  };

  const displayVertIcon = (repairBarcodeParam: string) => (
    <IconButton
      aria-label="more"
      aria-controls="long-menu"
      aria-haspopup="true"
      id="table-kabob"
      onClick={(e) => handleActionMenuClick(e, repairBarcodeParam)}
    >
      <MoreVertIcon />
    </IconButton>
  );

  const boxFetcher = (url: string, trackNumParam: string) => axios.get<IScannedBox>(url, {
    params: {
      trackingNumber: trackNumParam,
    },
  }).then((res) => res.data)
    .catch((e: AxiosError) => {
      if (e.code === '404') {
        setTrackingNumber(undefined);
        navigate('/box-detail');
      } else {
        setTrackingNumber(undefined);
        navigate('/box-detail');
        enqueueSnackbar(e?.response?.data, { persist: false, variant: 'error' });
      }
    });

  const boxScanFetcher = (url: string, trackNumParam: string) => axios.get<IScan[]>(url, {
    params: {
      trackingNumber: trackNumParam,
    },
  }).then((res) => res.data)
    .catch((e: AxiosError) => {
      enqueueSnackbar(e?.response?.data, { persist: false, variant: 'error' });
    });

  const boxRepairsFetcher = (url: string, trackNumParam: string) => axios.get<IBoxRepair[]>(url, {
    params: {
      trackingNumber: trackNumParam,
    },
  }).then((res) => res.data)
    .catch((e: AxiosError) => {
      enqueueSnackbar(e?.response?.data, { persist: false, variant: 'error' });
    });

  const getBoxId = (TrackNumInput: string) => {
    const boxIdReturn = axios.get<{ BoxId:string }>('/BoxLookup/BoxByTrackingNum', {
      params: {
        trackingNumber: TrackNumInput,
      },
    })
      .then((response) => (response.data.BoxId))
      .catch((error: AxiosError) => {
      });
    return boxIdReturn;
  };

  const getBox = debounce((TrackNumInput: string) => {
    if (TrackNumInput !== '') {
      setGetBoxProcessing(true);
      setTrackingNumber(TrackNumInput);
      getBoxId(TrackNumInput)
        .then(
          (result) => { if (result) setBoxId(result); },
        )
        .catch(
          (error) => enqueueSnackbar(error, { persist: false, variant: 'error' }),
        )
        .finally(() => setGetBoxProcessing(false));
    }
  }, 500);

  React.useEffect(() => {
    if (trackingNumber) { getBox(trackingNumber); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { data: boxInfo } = useSWR(trackingNumber ? ['/BoxLookup/BoxByTrackingNum', trackingNumber] : null, (url, trackNumParam) => boxFetcher(url, trackNumParam));
  const { data: boxScans } = useSWR(trackingNumber
    ? ['/BoxLookup/BoxScans', trackingNumber]
    : null, (url, trackNumParam) => boxScanFetcher(url, trackNumParam));
  const { data: boxRepairs } = useSWR(trackingNumber ? ['/BoxLookup/BoxRepairs', trackingNumber] : null, (url, trackNumParam) => boxRepairsFetcher(url, trackNumParam),
    {
      refreshInterval: 30000,
    });

  let bannerTrackNumBuffer = '';
  let honeyTrackNumBuffer = '';

  const handleBannerBarcodeInput = (e: KeyboardEvent) => {
    if (e.key !== 'Shift') {
      bannerTrackNumBuffer = `${bannerTrackNumBuffer}${e.key}`;
      if (bannerTrackNumBuffer.includes('In:')) {
        bannerTrackNumBuffer = '';
      }
      if (bannerTrackNumBuffer.includes('{*|}')) {
        getBox(bannerTrackNumBuffer.replace('{*|}', ''));
        bannerTrackNumBuffer = '';
        honeyTrackNumBuffer = '';
      }
    }
  };

  const handleHoneywellBarcodeInput = (e: KeyboardEvent) => {
    if (e.key && (e.key.length === 1 || e.key === 'Enter')) {
      if (e.key !== 'Enter') {
        honeyTrackNumBuffer = `${honeyTrackNumBuffer}${e.key}`;
      } else {
        if (!e.defaultPrevented) {
          honeyTrackNumBuffer.replace('{*|}', '');
          getBox(honeyTrackNumBuffer.replace('Enter', ''));
          honeyTrackNumBuffer = '';
          bannerTrackNumBuffer = '';
        }
        bannerTrackNumBuffer = '';
        honeyTrackNumBuffer = '';
      }
    }
  };

  useEventListener('keydown', handleBannerBarcodeInput);
  useEventListener('keydown', handleHoneywellBarcodeInput);

  const repairColumns : GridColumns = [
    { field: 'barcode', headerName: 'Barcode', flex: 1 },
    { field: 'storeRepairNumber', headerName: 'Store Repair Number', flex: 1.2 },
    { field: 'repairType', headerName: 'Repair Type', flex: 1 },
    { field: 'location', headerName: 'Location', flex: 1 },
    {
      field: 'startDate',
      headerName: 'Start Date',
      renderCell: ({ value }) => (
        ((typeof (value) === 'string')
          ? <>{new Date(value).toLocaleDateString() }</>
          : <>{value}</>
        )
      ),
      flex: 1,
    },
    {
      field: 'barcodeActions',
      type: 'string',
      headerName: 'Actions',
      flex: 0.5,
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
      valueGetter: ({ row }) => row.barcode as string,
      renderCell: ({ value }) => (
        ((typeof (value) === 'string')
          ? displayVertIcon(value)
          : <></>
        )
      ),
    },
  ];

  const repairRows = boxRepairs?.map((boxRepair) => ({
    repairId: boxRepair.repairId,
    barcode: boxRepair.barcode,
    storeRepairNumber: boxRepair.storeRepairNumber,
    location: boxRepair.location,
    startDate: boxRepair.startDate,
  }));

  const Repairs = (
    <Box>
      <Paper>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Box pl={2}>
              <Typography variant="h6">{'Repairs'}</Typography>
            </Box>
          </Grid>
          <Grid item xs={12}>
            {getBoxProcessing ? <CircularProgress /> : <Divider />}
          </Grid>
          <Grid item xs={12}>
            <Box style={{ height: '48vh', width: '100%' }}>
              <DataGrid
                getRowId={
                    (row) => (
                      // eslint-disable-next-line no-nested-ternary, @typescript-eslint/no-unsafe-return
                      row.repairId ? row.repairId : row.barcode ? row.barcode : '')
                }
                rows={repairRows || []}
                columns={repairColumns || []}
                hideFooter
              />
            </Box>
          </Grid>
        </Grid>
        {
              repairBarcode && anchorEl && (
                <ActionMenu
                  anchorEl={anchorEl}
                  handleClose={handleClose}
                >
                  <MenuItem
                    id="test-button"
                    onClick={() => navigate(`/repair-detail/${repairBarcode}`)}
                  >
                    {'View Repair Detail'}
                  </MenuItem>
                </ActionMenu>
              )
            }
      </Paper>
    </Box>
  );

  const boxNotesTableColumns: GridColumns = [
    {
      field: 'note',
      headerName: 'Note Detail',
      flex: 1,
    },
  ];
  const boxNotesTableRows = boxInfo?.boxNotes && boxInfo?.boxNotes.map((scan, index) => ({
    id: index || '',
    note: scan.note,
  }));
  const boxScanTableRows = boxScans && boxScans?.map((scan, index) => ({
    id: index || '',
    scanBody: scan.scanBody,
    scanType: scan.scanType,
    scanLocation: scan.scanLocation,
    created: new Date(`${scan.created}z`).toLocaleString(),
    scannedBy: scan.scannedBy,
  }));
  const boxScanTableColumns: GridColumns = [
    {
      field: 'scanBody',
      headerName: 'Tracking Number',
      flex: 0.75,
    },
    {
      field: 'scanType',
      headerName: 'Type',
      flex: 0.5,
    },
    {
      field: 'scanLocation',
      headerName: 'Scan Location',
      flex: 1,
    },
    {
      field: 'created',
      headerName: 'Created',
      flex: 1,
    },
    {
      field: 'scannedBy',
      headerName: 'Scanned By',
      flex: 1,
    },
  ];
  const BoxHistory = (
    <Grid item xs={12}>
      <Paper>
        <Card>
          <CardHeader
            title={(
              <Typography variant="h6" display="inline">{'Box Scan History'}</Typography>
            )}
            disableTypography
          />
          <CardContent>
            <DataGrid
              rows={boxScanTableRows || []}
              columns={boxScanTableColumns || []}
              disableColumnMenu
              autoHeight
              pageSize={50}
            />
          </CardContent>
        </Card>
      </Paper>
    </Grid>
  );
  const BoxNotes = (
    <Card>
      <Grid container justifyContent="space-between">
        <Grid item>
          <Box ml={2} my={2}>
            <Typography variant="h5" style={{ fontSize: '1.125rem' }}>{'Box Notes'}</Typography>
          </Box>
        </Grid>
      </Grid>
      <Divider />
      <Grid container justifyContent="space-between" xs={12}>
        <Grid item lg={12} md={12}>
          <DataGrid
            rows={boxNotesTableRows || []}
            columns={boxNotesTableColumns || []}
            autoHeight
          />
        </Grid>
      </Grid>
    </Card>
  );

  const BoxInfo: React.FC<{ box:IScannedBox }> = ({ box }) => (
    <Card style={{ width: '100%' }}>
      <Grid container justifyContent="space-between">
        <Grid item>
          <Box ml={2} my={2}>
            <Typography variant="h5" style={{ fontSize: '1.125rem' }}>{'Box Information'}</Typography>
          </Box>
        </Grid>
      </Grid>
      <Divider />
      <CardContent>
        <Grid
          container
          spacing={2}
        >
          { Boolean(box.trackingNumber)
            && (
            <Grid item lg={4} md={6} xs={12}>
              <BoxProperty
                title={box.trackingNumber}
                subtitle="Tracking Number"
              />
            </Grid>
            )}
          { Boolean(box.company)
            && (
            <Grid item lg={4} md={6} xs={12}>
              <BoxProperty
                title={box.company}
                subtitle="Company"
              />
            </Grid>
            )}
          { Boolean(box.store)
            && (
            <Grid item lg={4} md={6} xs={12}>
              <BoxProperty
                title={box.store}
                subtitle="Store"
              />
            </Grid>
            )}
          {
            (typeof box.createdDate === 'string')
            && (
            <Grid item lg={4} md={6} xs={12}>
              <BoxProperty
                title={new Date(`${box.createdDate}z`).toLocaleString()}
                subtitle="Received Date"
              />
            </Grid>
            )
            }
          {(typeof box.lastModified === 'string')
              && (
              <Grid item lg={4} md={6} xs={12}>
                <BoxProperty
                  title={new Date(`${box.lastModified}z`).toLocaleString()}
                  subtitle="Last Modified"
                />
              </Grid>
              )}
        </Grid>
      </CardContent>
    </Card>
  );

  if (!boxInfo) {
    return (
      <Grid container direction="column" spacing={2} alignItems="center" justifyContent="center">
        <Grid item>
          <Alert severity="info">{'Scan in tracking number to review box scan data and related repair data'}</Alert>
        </Grid>
        <Grid item>
          {getBoxProcessing
            ? (
              <CircularProgress />
            )
            : (
              <TextField
                variant="outlined"
                size="small"
                onKeyPress={
                  (event) => {
                    if (event.key === 'Enter') {
                      event.preventDefault();
                      event.stopPropagation();
                      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
                      getBox((event.target as HTMLInputElement).value);
                    }
                  }
                  }
              />
            )}
        </Grid>
      </Grid>
    );
  }

  return (
    <Box>
      <Grid container spacing={0}>
        <Grid container item spacing={1} sm={12} md={9}>
          <Grid container item spacing={2} sm={12} md={12}>
            <Grid item xs={12}>
              <Alert
                severity="info"
              >
                {'Review box information and repairs shipped in the box.'}
              </Alert>
            </Grid>
            <Grid item xs={12}>
              <BoxInfo box={boxInfo} />
            </Grid>
            <Grid item xs={12}>
              <Grid item xs={12}>
                {BoxHistory}
              </Grid>
            </Grid>
            <Grid item xs={12}>
              {Repairs}
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={3}>
          {Boolean(boxInfo.boxNotes) && BoxNotes}
        </Grid>
      </Grid>
      <Grid>
        <Divider orientation="vertical" />
      </Grid>
    </Box>
  );
};

export default BoxDetail;
